import {useCallback, useContext, useState} from 'react';


import {ValidationContext} from 'src/contexts/validation-context';
import {
    DUPLICATED_EMAIL,
    NO_USER_EMAIL,
    NO_USER_FIRST_NAME,
    NO_USER_LAST_NAME,
    SERVICE_UNAVAILABLE,
    UNVALID_EMAIL,
    VALIDATE_CONTROL_IDS,
    VALIDATION_ERRORS,
} from 'src/modules/profile/account/create-user/constants';
import {getUserByEmail} from 'src/openapi/admin/apis/GetUserByEmailApi';
import {useAppDispatch} from 'src/store/hooks';

import {isValidEmail} from '@pb/kit-util-is-valid-email';

import type {ValidationContextType} from 'src/contexts/validation-context';
import type {UserDetails} from 'src/openapi/admin/models/UserDetails';
import type {UserShortInfo} from 'src/openapi/admin/models/UserShortInfo';

export const useUserDetailsValidation = () => {
    const {addError, removeError} = useContext<ValidationContextType>(ValidationContext);
    const apiDispatch = useAppDispatch();
    const [existedUser, setExistedUser] = useState<UserShortInfo | null>(null);
    const [showMoveToUserProfileModal, setShowMoveToUserProfileModal] = useState(false);

    const validateUnfilledFields = useCallback((userUpdateRequest: UserDetails) => {
        let hasErrors = false;

        if (!userUpdateRequest.email) {
            addError({
                code: NO_USER_EMAIL,
                message: VALIDATION_ERRORS.NO_USER_EMAIL,
                controlId: VALIDATE_CONTROL_IDS.EMAIL,
            });
            hasErrors = true;
        }

        if (!userUpdateRequest.firstName) {
            addError({
                code: NO_USER_FIRST_NAME,
                message: VALIDATION_ERRORS.NO_USER_FIRST_NAME,
                controlId: VALIDATE_CONTROL_IDS.FIRST_NAME,
            });
            hasErrors = true;
        }

        if (!userUpdateRequest.lastName) {
            addError({
                code: NO_USER_LAST_NAME,
                message: VALIDATION_ERRORS.NO_USER_LAST_NAME,
                controlId: VALIDATE_CONTROL_IDS.LAST_NAME,
            });
            hasErrors = true;
        }

        return hasErrors;
    }, [addError]);

    const isDuplicatedEmail = useCallback(async (email: string, userId?: number) => {
        setExistedUser(null);
        const res = await apiDispatch(getUserByEmail({userEmail: email}));

        let isExistedEmail = false;

        if (res.payload.success && res.payload.data.userShortInfo && (
            !userId || res.payload.data.userShortInfo.userId !== userId
        )) {
            isExistedEmail = res.payload.data.userShortInfo.email.toLowerCase() === email.trim().toLowerCase();
            setExistedUser(res.payload.data.userShortInfo);
        }

        return {
            unexpectedError: res.payload.error,
            isExistedEmail,
        };
    }, [apiDispatch]);

    const closeMoveToUserProfileModal = useCallback(() => {
        setShowMoveToUserProfileModal(false);

        addError({
            code: DUPLICATED_EMAIL,
            message: VALIDATION_ERRORS.DUPLICATED_EMAIL,
            controlId: VALIDATE_CONTROL_IDS.EMAIL,
        });
    }, [addError]);

    const validateEmailOnChange = useCallback(async (value: string, userId?: number) => {
        removeError(VALIDATE_CONTROL_IDS.EMAIL);

        if (!value) {
            return false;
        }

        if (isValidEmail(value)) {
            const {isExistedEmail, unexpectedError} = await isDuplicatedEmail(value, userId);
            if (unexpectedError) {
                addError({
                    code: SERVICE_UNAVAILABLE,
                    message: VALIDATION_ERRORS.SERVICE_UNAVAILABLE,
                    controlId: VALIDATE_CONTROL_IDS.EMAIL,
                });
                return true;
            } else if (isExistedEmail) {
                setShowMoveToUserProfileModal(true);
                return true;
            }
        } else {
            addError({
                code: UNVALID_EMAIL,
                message: VALIDATION_ERRORS.UNVALID_EMAIL,
                controlId: VALIDATE_CONTROL_IDS.EMAIL,
            });
            return true;
        }

        return false;
    }, [addError, isDuplicatedEmail, removeError]);

    const validateFirstNameOnChange = useCallback((firstName: string) => {
        if (firstName) {
            removeError(VALIDATE_CONTROL_IDS.FIRST_NAME, [NO_USER_FIRST_NAME]);
        }
    }, [removeError]);

    const validateLastNameOnChange = useCallback((lastName: string) => {
        if (lastName) {
            removeError(VALIDATE_CONTROL_IDS.LAST_NAME, [NO_USER_LAST_NAME]);
        }
    }, [removeError]);

    return {
        existedUser,
        showMoveToUserProfileModal,
        closeMoveToUserProfileModal,
        validateUnfilledFields,
        validateEmailOnChange,
        validateFirstNameOnChange,
        validateLastNameOnChange,
    };
};
